
import React from 'react'
import { NODE_LAYOUT } from '@genieartinc/schemas'
import { HandleIcon } from './HandleIcon'
import {
  Handle,
  Position,
  useStore,
  type HandleProps,
} from '@xyflow/react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// Keep React Flow's hitbox stable at every zoom. Only its inner glyph changes.
const ZOOM_THRESHOLD_SMALL = 0.6

// 2px of the tab overlaps the node edge so the port meets the border instead of floating outside it.
const HANDLE_INSET = 2

const ANCHORS: Record<Position, React.CSSProperties> = {
  [Position.Left]: { left: HANDLE_INSET, transform: 'translate(-100%, -50%)' },
  [Position.Right]: { right: HANDLE_INSET, transform: 'translate(100%, -50%)' },
  [Position.Top]: { top: HANDLE_INSET, transform: 'translate(-50%, -100%)' },
  [Position.Bottom]: { bottom: HANDLE_INSET, transform: 'translate(-50%, 100%)' },
}

/* ---- Props ---- */
export interface GenieHandleProps extends Omit<HandleProps, 'position'> {
  position?: Position
  label?: string
  isConnected?: boolean
  compact?: boolean
  icon?: string
  /** Opacity override (0-1) for compatibility feedback during drag */
  compatibilityOpacity?: number
}

function GenieHandleComponent({
  type,
  position = type === 'source' ? Position.Right : Position.Left,
  id,
  label,
  icon,
  isConnected = false,
  compact = false,
  compatibilityOpacity,
  style,
  ...rest
}: GenieHandleProps) {
  const zoomSmall = useStore((s) => s.transform[2] < ZOOM_THRESHOLD_SMALL)
  const isSmall = compact || zoomSmall

  const isLeft = position === Position.Left
  const isRight = position === Position.Right
  const tooltipSide = isLeft ? 'left' : isRight ? 'right' : position === Position.Top ? 'top' : 'bottom'

  // Rounded rectangle: left corners rounded for left handles, right corners for right handles
  const borderRadius = isLeft
      ? '6px 0 0 6px'   // left-top, right-top, right-bottom, left-bottom
      : isRight
        ? '0 6px 6px 0'
        : '6px'

  const handleStyle: React.CSSProperties = {
    width: NODE_LAYOUT.handleSize,
    height: NODE_LAYOUT.handleSize,
    border: 'none',
    backgroundColor: isSmall ? 'transparent' : 'var(--scaffold-bg, #171717)',
    borderRadius,
    ...ANCHORS[position],
    color: 'var(--scaffold-text, #fff)',
    opacity: compatibilityOpacity ?? 1,
    transition: 'opacity 0.15s ease, background-color 0.2s ease',
    cursor: 'crosshair',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Handle
            type={type}
            position={position}
            id={id}
            aria-label={`${type === 'source' ? 'Output' : 'Input'}: ${label ?? id ?? 'connection'}`}
            data-connected={isConnected || undefined}
            style={handleStyle}
            {...rest}
          >
            {isSmall ? (
              <span className="pointer-events-none h-2 w-2 rounded-full bg-current" />
            ) : icon ? (
              <HandleIcon name={icon} size={14} />
            ) : (
              <span className="pointer-events-none h-2 w-2 rounded-full border border-current" />
            )}
          </Handle>
        </TooltipTrigger>
        {label && (
          <TooltipContent side={tooltipSide} className="text-xs">
            {label}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  )
}

export const GenieHandle = React.memo(GenieHandleComponent)
GenieHandle.displayName = 'GenieHandle'
